<template>
	<div class="modal fade" id="terminalModal" ref="terminalModal" tabindex="-1" aria-labelledby="terminalModalLabel" aria-hidden="true">
		<div class="modal-dialog modal-xl modal-dialog-scrollable">
			<div class="modal-content terminal-modal">
				<div class="modal-header">
					<div>
						<h5 class="modal-title" id="terminalModalLabel">Artisan Terminal</h5>
						<small class="text-muted">Run single `php artisan` commands and inspect the output.</small>
					</div>
					<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
				</div>
				<div class="modal-body">
					<div class="mb-3">
						<label for="terminal-command" class="form-label">Command</label>
						<div class="input-group">
							<span class="input-group-text terminal-prefix">php artisan</span>
							<input
								id="terminal-command"
								v-model.trim="command"
								type="text"
								class="form-control"
								placeholder="migrate --force"
								:disabled="loading"
								@keydown.enter.prevent="runCommand"
							>
							<button class="btn btn-primary" type="button" :disabled="loading || !command" @click="runCommand">
								<span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
								Run
							</button>
						</div>
						<div class="form-text">`tinker` is intentionally blocked. Everything else runs as a single Artisan command.</div>
					</div>

					<div class="mb-3">
						<div class="d-flex flex-wrap gap-2">
							<button v-for="preset in presets" :key="preset" type="button" class="btn btn-outline-secondary btn-sm" :disabled="loading" @click="applyPreset(preset)">
								{{ preset }}
							</button>
						</div>
					</div>

					<div v-if="lastResult" class="terminal-meta mb-3">
						<div class="terminal-meta-item">
							<span class="meta-label">Status</span>
							<span :class="lastResult.success ? 'badge bg-success' : 'badge bg-danger'">
								{{ lastResult.success ? 'Success' : 'Failed' }}
							</span>
						</div>
						<div class="terminal-meta-item">
							<span class="meta-label">Exit Code</span>
							<span>{{ lastResult.exit_code }}</span>
						</div>
						<div class="terminal-meta-item">
							<span class="meta-label">Duration</span>
							<span>{{ formatDuration(lastResult.duration_ms) }}</span>
						</div>
						<div class="terminal-meta-item">
							<span class="meta-label">Executed</span>
							<span>{{ formatTimestamp(lastResult.executed_at) }}</span>
						</div>
					</div>

					<div class="row g-3">
						<div class="col-lg-8">
							<div class="terminal-panel">
								<div class="terminal-panel-header">
									<span>Output</span>
									<span v-if="lastResult && lastResult.output_truncated" class="badge bg-warning text-dark">Truncated</span>
								</div>
								<pre class="terminal-output">{{ renderedOutput }}</pre>
							</div>
						</div>
						<div class="col-lg-4">
							<div class="terminal-panel">
								<div class="terminal-panel-header">
									<span>Recent Commands</span>
								</div>
								<div v-if="history.length" class="terminal-history">
									<button
										v-for="entry in history"
										:key="entry.id"
										type="button"
										class="terminal-history-item"
										:disabled="loading"
										@click="command = entry.command"
									>
										<div class="history-command">php artisan {{ entry.command }}</div>
										<div class="history-meta">
											<span>{{ entry.success ? 'Success' : 'Failed' }}</span>
											<span>{{ formatTimestamp(entry.executed_at) }}</span>
										</div>
									</button>
								</div>
								<div v-else class="terminal-history-empty">
									No commands executed yet.
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="modal-footer">
					<button type="button" class="btn btn-outline-secondary" :disabled="loading" @click="clearOutput">Clear Output</button>
					<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { Modal } from 'bootstrap'
import restApi from '@/api/index'

const HISTORY_STORAGE_KEY = 'terminal-modal-history'
const MAX_HISTORY_ITEMS = 12

export default {
	name: 'TerminalModal',
	data() {
		return {
			modal: null,
			command: '',
			loading: false,
			lastResult: null,
			history: [],
			presets: ['optimize:clear', 'cache:clear', 'config:clear', 'route:clear', 'view:clear', 'migrate:status', 'queue:restart'],
		}
	},
	computed: {
		renderedOutput() {
			if (this.loading) {
				return 'Running command...'
			}

			if (!this.lastResult) {
				return 'No output yet.'
			}

			const sections = []

			if (this.lastResult.stdout) {
				sections.push(this.lastResult.stdout.trimEnd())
			}

			if (this.lastResult.stderr) {
				sections.push(`[stderr]\n${this.lastResult.stderr.trimEnd()}`)
			}

			if (!sections.length) {
				return 'Command finished with no output.'
			}

			return sections.join('\n\n')
		},
	},
	mounted() {
		this.modal = new Modal(this.$refs.terminalModal)
		this.history = this.loadHistory()

		this.$refs.terminalModal.addEventListener('shown.bs.modal', () => {
			this.$nextTick(() => {
				const input = this.$el.querySelector('#terminal-command')
				if (input) {
					input.focus()
				}
			})
		})
	},
	methods: {
		showModal() {
			this.modal.show()
		},
		applyPreset(command) {
			this.command = command
		},
		clearOutput() {
			this.lastResult = null
		},
		formatDuration(durationMs) {
			if (durationMs < 1000) {
				return `${durationMs}ms`
			}

			return `${(durationMs / 1000).toFixed(2)}s`
		},
		formatTimestamp(timestamp) {
			if (!timestamp) {
				return '-'
			}

			return new Date(timestamp).toLocaleString()
		},
		loadHistory() {
			try {
				const stored = localStorage.getItem(HISTORY_STORAGE_KEY)
				return stored ? JSON.parse(stored) : []
			} catch (error) {
				console.warn('Failed to parse terminal history', error)
				return []
			}
		},
		persistHistory() {
			localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(this.history))
		},
		pushHistory(result) {
			const entry = {
				id: `${result.executed_at}-${result.command}`,
				command: this.command,
				success: result.success,
				executed_at: result.executed_at,
			}

			this.history = [entry, ...this.history.filter(item => item.command !== entry.command)].slice(0, MAX_HISTORY_ITEMS)
			this.persistHistory()
		},
		async runCommand() {
			if (!this.command || this.loading) {
				return
			}

			this.loading = true

			try {
				const response = await restApi.post('/terminal/artisan/execute', {
					command: this.command,
				})

				this.lastResult = response.data
				this.pushHistory(response.data)
			} catch (error) {
				const responseData = error.response?.data || {}
				this.lastResult = {
					success: false,
					command: `php artisan ${this.command}`,
					exit_code: error.response?.status || 500,
					duration_ms: 0,
					executed_at: new Date().toISOString(),
					stdout: '',
					stderr: responseData.message || 'Command execution failed.',
					output_truncated: false,
				}
			} finally {
				this.loading = false
			}
		},
	},
}
</script>

<style scoped>
.terminal-modal {
	border: 0;
}

.terminal-prefix {
	background: #111827;
	color: #f9fafb;
	border-color: #111827;
	font-family: Monaco, Menlo, Consolas, 'Courier New', monospace;
}

.terminal-meta {
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
}

.terminal-meta-item {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 14px;
}

.meta-label {
	font-weight: 600;
	color: #64748b;
}

.terminal-panel {
	border: 1px solid #dbe3ee;
	border-radius: 12px;
	overflow: hidden;
	background: #ffffff;
}

.terminal-panel-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 16px;
	border-bottom: 1px solid #e5e7eb;
	font-weight: 600;
}

.terminal-output {
	margin: 0;
	padding: 16px;
	min-height: 420px;
	max-height: 60vh;
	overflow: auto;
	background: #0f172a;
	color: #e2e8f0;
	font-size: 13px;
	line-height: 1.5;
	font-family: Monaco, Menlo, Consolas, 'Courier New', monospace;
	white-space: pre-wrap;
	word-break: break-word;
}

.terminal-history {
	display: flex;
	flex-direction: column;
	max-height: 60vh;
	overflow: auto;
}

.terminal-history-item {
	width: 100%;
	padding: 12px 16px;
	border: 0;
	border-bottom: 1px solid #e5e7eb;
	background: #ffffff;
	text-align: left;
	transition: background-color 0.2s ease;
}

.terminal-history-item:hover {
	background: #f8fafc;
}

.history-command {
	font-family: Monaco, Menlo, Consolas, 'Courier New', monospace;
	font-size: 13px;
	color: #0f172a;
}

.history-meta {
	display: flex;
	justify-content: space-between;
	margin-top: 6px;
	font-size: 12px;
	color: #64748b;
}

.terminal-history-empty {
	padding: 16px;
	color: #64748b;
}
</style>
